<template>
  <div>
    <div v-if="loading">
      <MainInfoSkeleton />
    </div>
    <div v-else class="main-info">
      <div class="main-info_header">
        <img
          v-if="contractLogo && isContract"
          :src="contractLogo"
          alt="Contract Logo"
          class="main-info_header_icon"
        />
        <div class="main-info_header_left">
          <BaseSkeleton
            v-if="loading"
            class="w-[36px] h-[36px] rounded-[50%]"
          />
          <BaseSvgIcon
            v-else-if="hasValidIcon"
            :name="icon"
            class="main-info_header_icon"
          />
          <div class="main-info_header_titles">
            <BaseSkeleton
              v-if="loading"
              class="w-[120px] h-[18px] rounded-md"
            />
            <Text
              v-else-if="title"
              class="main-info__title"
              pattern="body1"
              :weight="600"
              :height="110"
            >
              {{ $t("Amount") }}
            </Text>
            <Text
              v-if="contractName && isContract"
              pattern="body3"
              :weight="600"
              :height="110"
              color="#A3A3A3"
            >
              {{ contractName }}
              <span v-if="contractSymbol">({{ contractSymbol }})</span>
            </Text>
            <Text
              v-else-if="subtitle"
              pattern="body3"
              :weight="600"
              :height="110"
              color="#A3A3A3"
            >
              {{ $t(subtitle) }}
            </Text>
          </div>
          <div v-if="isContract" class="main-info_header_contract">
            <BaseSvgIcon
              name="colored-check-green"
              class="main-info_header_contract_icon"
            />
            <Text pattern="body3" :weight="600" :height="100" color="#fff">
              {{ $t("Contract") }}
            </Text>
          </div>
        </div>
      </div>
      <div class="main-info__balance">
        <div class="main-info__balance-amount">
          {{ amountData?.amount }}
        </div>
        <div class="main-info__balance-usd-value">
          {{ amountData?.usd_value }}
        </div>
      </div>
      <div v-if="reactiveDetails?.length > 0" class="main-info_details">
        <div
          v-for="{
            id,
            label,
            value,
            underline,
            isStatus,
            block,
            tooltip,
            copy,
            smartContract,
            links,
          } in reactiveDetails"
          :key="id"
          class="main-info_details_item"
        >
          <div class="main-info_details_item_label">
            <Text pattern="body2" color="#787878" :weight="500" :height="150">
              {{ $t(label) }}
            </Text>
            <BasePopover
              v-if="tooltip"
              popoverType="tooltip"
              placement="bottom"
            >
              <template #trigger>
                <BaseSvgIcon name="info-gray" />
              </template>
              <template #content>
                {{ $t(tooltip) }}
              </template>
            </BasePopover>
          </div>

          <template v-if="isStatus">
            <div class="status-row">
              <BaseSvgIcon
                :name="getStatusIcon(value)"
                class="main-info_details_icon"
              />
              <Text pattern="body2" :weight="600" :height="135">
                <span v-html="getStatusTitle(value, block)" />
              </Text>
            </div>
          </template>

          <template v-else-if="links?.length > 0">
            <div class="main-info_details_item_links">
              <a
                v-for="{ id, label, href } in links"
                :key="id"
                :href="href"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Text
                  pattern="body2"
                  :weight="600"
                  :height="135"
                  textDecoration="underline"
                >
                  {{ $t(label) }}
                </Text>
              </a>
            </div>
          </template>

          <template v-else>
            <div class="main-info_details_item_value">
              <Text
                pattern="body2"
                :weight="600"
                :height="135"
                :textDecoration="underline ? 'underline' : 'none'"
                class="main-info_details_item_value_text"
              >
                {{ value }}
                <span
                  v-if="label === 'Amount' && value.includes('$')"
                  class="annotation"
                >
                  {{ $t("MainInfo | Amount | currentRate") }}
                </span>
              </Text>
              <BaseSvgIcon
                v-if="copy"
                name="copy"
                class="copy"
                @click="copyToClipboard(value)"
              />
              <div v-if="smartContract" class="contract-label">
                <Text pattern="body2" :weight="600" :height="100" color="#fff">
                  SC
                </Text>
              </div>
            </div>
          </template>
        </div>
        <div class="main-info_details_buttons">
          <BaseButton
            type="shade"
            outline
            class="main-info_details_button"
            @click="copyTransaction(copyValue)"
          >
            <BaseSvgIcon name="copy" class="icon" />
            {{ $t("Transaction | Copy hash") }}
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance } from "vue";
import { useI18n } from "vue-i18n";

// components
import { Text, BaseSvgIcon, BasePopover, BaseButton } from "@/shared/ui";
import BaseSkeleton from "@/shared/ui/BaseSkeleton/BaseSkeleton.vue";
import MainInfoSkeleton from "./MainInfoSkeleton.vue";
// utils
import { copyToClipboard } from "@emcd/common/helpers";

// helpers
import { comma } from "@/shared/utils/format";

// icons
import "@/assets/icons/SVG/bitcoin.svg";
import "@/assets/icons/SVG/tron.svg";
import "@/assets/icons/SVG/usdt.svg";
import "@/assets/icons/SVG/ton.svg";
import "@/assets/icons/SVG/ethereum.svg";
import "@/assets/icons/SVG/colored-check-green.svg";
import "@/assets/icons/SVG/pending.svg";
import "@/assets/icons/SVG/error2.svg";
import "@/assets/icons/SVG/success2.svg";
import "@/assets/icons/SVG/info-gray.svg";
import "@/assets/icons/SVG/copy.svg";
import "@/assets/icons/SVG/share.svg";

import { NETWORK_ENUMS } from "@/shared/helpers";
import { formatDateMonthInFront } from "@/shared/utils/format/date";

// composables
const { t, locale } = useI18n();
const { proxy } = getCurrentInstance();

// props
const props = defineProps({
  icon: {
    type: String,
    default: "",
    validator: (value) =>
      [
        NETWORK_ENUMS.bitcoin,
        NETWORK_ENUMS.tron,
        NETWORK_ENUMS.usdt,
        NETWORK_ENUMS.ton,
        NETWORK_ENUMS.ethereum,
      ].includes(value),
  },
  title: {
    type: String,
    default: "",
  },
  subtitle: {
    type: String,
    default: "",
  },
  isContract: {
    type: Boolean,
    default: false,
  },
  details: {
    type: Array,
    default: () => [],
  },
  amountData: {
    type: Object,
    default: null,
  },
  copyValue: {
    type: String,
    default: "",
  },
  loading: {
    type: Boolean,
    default: true,
  },
  contractName: {
    type: String,
    default: "",
  },
  contractSymbol: {
    type: String,
    default: "",
  },
  contractLogo: {
    type: String,
    default: "",
  },
});

// computed
const hasValidIcon = computed(() => {
  const availableIcons = [
    NETWORK_ENUMS.bitcoin,
    NETWORK_ENUMS.tron,
    NETWORK_ENUMS.usdt,
    NETWORK_ENUMS.ton,
    NETWORK_ENUMS.ethereum,
  ];
  return props.icon && availableIcons.includes(props.icon);
});

// Реактивное форматирование деталей с учетом текущей локали
const reactiveDetails = computed(() => {
  if (!props.details || !Array.isArray(props.details)) {
    return [];
  }

  return props.details.map((detail) => {
    if (detail.label === "Time" && detail.value) {
      return {
        ...detail,
        value: formatDateMonthInFront(detail.value, locale.value),
      };
    }
    return detail;
  });
});

// methods
const getStatusIcon = (status) => {
  switch (status) {
    case "pending":
      return "pending";
    case "error":
      return "error2";
    case "success":
      return "success2";
    default:
      return "";
  }
};

const getStatusTitle = (status, block) => {
  switch (status) {
    case "pending":
      return t("Waiting");
    case "error":
      return t("Error");
    case "success":
      return t("Inblock", { block: comma(block) });
    default:
      return "";
  }
};

const copyTransaction = (copyValue) => {
  copyToClipboard(copyValue, () => {});
  proxy.$toast({
    type: "success",
    title: t("TransactionCopied"),
  });
  window.emcd.AnalyticsService.track("click", {
    event_type: "click",
    product: "explorer",
    screen: "transaction",
    button_name: "copy_tx_hash",
  });
};
</script>

<style lang="scss" scoped>
.main-info {
  display: flex;
  flex-direction: column;
  background: var(--Dark-900, #111);
  width: 100%;
  border-radius: 10px;
  border: 1px solid var(--Dark-850, #1a1a1a);
  overflow: hidden;
  position: relative;
  // z-index: 1;

  &__title {
    color: #7a7a7a !important;
    font-size: 14px;
    font-weight: 500 !important;
    line-height: 150% !important;
    margin-bottom: 4px;
  }

  &__balance {
    padding: 0 32px;

    &-amount {
      font-size: 20px;
      font-weight: 600;
      line-height: 150%;
      color: #fafafa;
    }

    &-usd-value {
      font-size: 14px;
      font-weight: 500;
      line-height: 150%;
      color: #7a7a7a;
    }
  }

  &_header {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    padding: 32px 32px 0;
    overflow: hidden;

    &_left {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
      gap: 13px;
    }

    &_titles {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 4px;
      margin-right: 12px;
    }

    &_contract {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 6px 6.5px;
      gap: 6px;
      border-radius: 50px;
      background: var(--green-850);
      height: fit-content;

      &_icon {
        width: 23px;
        height: 23px;
      }
    }

    &_icon {
      position: absolute;
      right: 32px;
      top: 32px;
      width: 36px;
      height: 36px;
      pointer-events: none;
    }
  }

  &_details {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    padding: 20px 32px 32px;

    &_buttons {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 8px;
    }

    &_button {
      width: 100%;
      justify-items: center;
      align-items: center;
      padding: 11px 2px;
      height: 36px;
      color: var(--Basic-White, #fff);
      text-align: center;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 106%;
      gap: 10px;
      background-color: #1a1a1a;
      border-radius: 10px;
      border: none;

      .icon {
        width: 16px;
        height: 16px;
        pointer-events: none;
      }

      &:hover {
        background: #ffffff;
        color: #000000;

        .icon {
          transition: all 0.35s ease;
          filter: brightness(0) saturate(100%);
        }
      }

      &:active {
        background: var(--light-500);
        color: #000000;
      }
    }

    &_item {
      display: flex;
      flex-direction: column;

      &_label {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        gap: 3px;
        margin-bottom: 4px;
      }

      &_value {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        gap: 8px;

        .annotation {
          white-space: nowrap;
        }
      }

      &_value_text {
        font-weight: 500 !important;
        line-height: 150% !important;
      }

      &_links {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        gap: 4px;
      }

      .underline {
        text-decoration: underline;
      }

      .status-row {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        gap: 4px;
      }

      .copy {
        min-width: 20px;
        min-height: 20px;
        transition: color 0.3s;
        cursor: pointer;
        color: #c18afd;

        &:hover {
          color: #fff;
        }
      }

      .contract-label {
        user-select: none;
        border-radius: 11px;
        border: 1px solid var(--Dark-700, #2d2d2d);
        padding: 3px 8px;
      }
    }
  }
}
</style>
